import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import * as PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";
import { translateGridText } from "../../i18n";
import { surveyDatumPropType } from "../../propTypes";
import { formatLogicalCell, formatNumericCell } from "../../utilities";

const columnDefinitions = (t) => [
    {
        headerName: t("Division numeric code"),
        field: "divisionNumericCode",
        type: "numericColumn",
        width: 200,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Region code"),
        field: "regionCode",
        width: 150,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Possible outlier"),
        field: "possible_outlier",
        valueFormatter: (params) => formatLogicalCell(params.value),
        width: 175,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Possible outlier (user input)"),
        field: "possible_outlier_userinput",
        valueFormatter: (params) => formatLogicalCell(params.value),
        width: 225,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Start year"),
        field: "startDate",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 2),
        width: 125,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("End year"),
        field: "endDate",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 2),
        width: 125,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("In union?"),
        field: "isInUnion",
        valueFormatter: (params) => formatLogicalCell(params.value),
        width: 125,
        headerClass: "ag-left-aligned-header",
        cellStyle: { textAlign: "center" },
    },
    {
        headerName: t("Age"),
        field: "ageRange",
        width: 125,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Data series/type"),
        field: "dataSeriesType",
        width: 175,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Population"),
        field: "groupTypeRelativeToBaseline",
        headerTooltip: t("Group type relative to baseline"),
        width: 150,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Modern contraceptive use"),
        field: "contraceptiveUseModern",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 3),
        headerTooltip: t("Contraceptive use (modern)"),
        width: 225,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Traditional contraceptive use"),
        field: "contraceptiveUseTraditional",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 3),
        headerTooltip: t("Contraceptive use (traditional)"),
        width: 250,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("All contraceptive use"),
        field: "contraceptiveUseAny",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 3),
        headerTooltip: t("Contraceptive use (all)"),
        width: 225,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Modern methods unmet need"),
        field: "unmetNeedModern",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 3),
        headerTooltip: t("Unmet need for any method"),
        width: 250,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Unmet need for any method"),
        field: "unmetNeedAny",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 3),
        headerTooltip: t("Unmet need for any method"),
        width: 250,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Pertaining to methods used since last pregnancy"),
        field: "pertainingToMethodsUsedSinceLastPregnancyReason",
        width: 350,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Geographical bias"),
        field: "geographicalRegionBiasReason",
        width: 200,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Non-pregnant and other positive biases"),
        field: "nonPregnantAndOtherPositiveBiasesReason",
        width: 300,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Age bias"),
        field: "ageGroupBias",
        width: 150,
        headerClass: "ag-left-aligned-header",
        cellStyle: { textAlign: "center" },
    },
    {
        headerName: t("Modern method bias"),
        field: "modernMethodBias",
        width: 200,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Traditional method bias"),
        field: "traditionalMethodBiasReason",
        width: 200,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Absence of probing question bias?"),
        field: "hasAbsenceOfProbingQuestionsBias",
        valueFormatter: (params) => formatLogicalCell(params.value),
        width: 275,
        headerClass: "ag-left-aligned-header",
        cellStyle: { textAlign: "center" },
    },
    {
        headerName: t("SE log-ratio modern/no use"),
        field: "seLogRModernNoUse",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 2),
        headerTooltip: t("Sampling error for the log-ratio of modern contraceptive method use to no use"),
        width: 225,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("SE log-ratio traditional/no use"),
        field: "seLogRTraditionalNoUse",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 2),
        headerTooltip: t("Sampling error for the log-ratio of traditional contraceptive method use to no use"),
        width: 250,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("SE log-ratio unmet/no need"),
        field: "seLogRUnmetNoNeed",
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => formatNumericCell(params.value, 2),
        headerTooltip: t("Sampling error for the log-ratio of unmet need (any method) to no need"),
        width: 225,
        headerClass: "ag-left-aligned-header",
    },
    {
        headerName: t("Source ID"),
        field: "sourceId",
        type: "numericColumn",
        width: 125,
        headerClass: "ag-left-aligned-header",
    },
];

const SurveyDataTable = (props) => {
    const { t } = useTranslation();

    const rowData = props.data.map((item) => ({
        ...item,
        divisionNumericCode: String(item.divisionNumericCode).padStart(3, "0"),
    }));
    const columnDefs = columnDefinitions(t);

    return (
        <div className="ag-theme-material">
            <AgGridReact
                domLayout="autoHeight"
                defaultColDef={{ sortable: true, filter: true }}
                pagination={true}
                paginationPageSize={10}
                suppressColumnVirtualisation={true}
                suppressCellFocus={true}
                overlayNoRowsTemplate="<span />"
                getLocaleText={translateGridText}
                rowData={rowData}
                columnDefs={columnDefs}
                rowHeight={30}
                // autoSizeStrategy={{ type: "fitGridWidth" }}
                // onGridReady={(params) => params.columnApi.autoSizeAllColumns()}
            />
        </div>
    );
};

SurveyDataTable.propTypes = {
    data: PropTypes.arrayOf(surveyDatumPropType),
};

SurveyDataTable.defaultProps = {
    data: [],
};

export default SurveyDataTable;
